<template>
  <vd-dialog
    class="abtest-test-device-assignation-dialog-component"
    :visible="visible"
    title="Test device assignation"
    @close="onCloseDialog"
  >
    <TestDeviceSelectInput v-model:no-data="displayUserSettingsLink" v-model="selectedTestDevice" :game-id="gameId" />

    <div class="dialog-actions">
      <div>
        <span v-if="displayUserSettingsLink">
          You don't have device registered yet. Add them
          <router-link :to="addDeviceRoute">here</router-link>
          .
        </span>
      </div>
      <vd-button type="primary" :disabled="!selectedTestDevice || loading" :loading="loading" @click="onAddTestDevice">
        Assign this device
      </vd-button>
    </div>
  </vd-dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

import TestDeviceSelectInput from "@/components/TestDeviceSelectInput/TestDeviceSelectInput.vue";
import { useAbtestTestDeviceAssignationDialogAddTestDeviceMutation, useMutationResponse } from "@/graphql";
import useMessages from "@/hooks/messages";
import links from "@/router/links";

export default defineComponent({
  name: "AbtestTestDeviceAssignationDialog",

  components: {
    TestDeviceSelectInput,
  },

  props: {
    gameId: {
      type: String,
      required: true,
    },

    versionId: {
      type: String,
      required: true,
    },

    abtestId: {
      type: String,
      required: true,
    },

    cohortId: {
      type: String,
      required: true,
    },

    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close"],

  setup(props, { emit }) {
    const selectedTestDevice = ref<string | null>(null);
    const displayUserSettingsLink = ref(false);
    const addDeviceRoute = { name: links.addDevice };
    const { confirm } = useMessages();

    const { mutate: addTestDevice, loading } = useAbtestTestDeviceAssignationDialogAddTestDeviceMutation({});

    const onCloseDialog = () => {
      selectedTestDevice.value = null;
      emit("close");
    };

    const onAddTestDevice = async () => {
      const confirmed = await confirm(
        "Are you sure you want to assign this device to this cohort? If another device is already assigned to another cohort of the same layer, it will be replaced by the one you're currently selecting",
        {
          type: "warning",
        }
      );

      if (!selectedTestDevice.value || !confirmed) {
        return;
      }

      const response = await addTestDevice({
        gameId: props.gameId,
        versionId: props.versionId,
        abtestCohortId: props.cohortId,
        deviceId: selectedTestDevice.value,
      });

      const data = useMutationResponse(response, (resp) => resp?.addTestDeviceToCohort);

      if (!loading.value && data?.success) {
        onCloseDialog();
      }
    };

    return {
      selectedTestDevice,
      onCloseDialog,
      onAddTestDevice,
      loading,
      displayUserSettingsLink,
      addDeviceRoute,
    };
  },
});
</script>

<style lang="scss" scoped>
.abtest-test-device-assignation-dialog-component {
  .dialog-actions {
    margin-top: size("L");
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
